import { useState, useCallback, useRef } from 'react';
import { applyGroupedSelectionClick } from '../utils/rangeSelection';

/**
 * Observation-level (leaf-row) multi-select for the My Files group lists.
 *
 * The selectable unit is the row that owns a FileGrid — an observation in the
 * By Project / By Target tabs, a target in the By Date tab. Selection is global
 * across the whole tab so a single "Delete selected" can span groups via
 * ctrl/cmd-click, while shift-click ranges stay within the group the click
 * happened in (see applyGroupedSelectionClick).
 *
 * Each row's file ids are captured at click time (from the per-group
 * `fileIdsByKey` map) so getSelectedFileIds() can resolve the concrete files to
 * delete later, even after the owning group has been collapsed.
 */
export const useObservationSelection = () => {
  const [state, setState] = useState({ selected: new Set(), anchor: null });
  const fileIdsRef = useRef(new Map()); // rowKey -> string[]

  const handleClick = useCallback((containerId, index, orderedKeys, fileIdsByKey, event) => {
    const modifiers = {
      shift: !!event?.shiftKey,
      ctrlOrMeta: !!(event?.ctrlKey || event?.metaKey),
    };
    setState((prev) => {
      const next = applyGroupedSelectionClick(prev, containerId, index, orderedKeys, modifiers);
      // Remember the files behind every now-selected row we can resolve.
      next.selected.forEach((key) => {
        if (!fileIdsRef.current.has(key) && fileIdsByKey?.[key]) {
          fileIdsRef.current.set(key, fileIdsByKey[key]);
        }
      });
      return next;
    });
  }, []);

  const clear = useCallback(() => setState({ selected: new Set(), anchor: null }), []);

  const isSelected = useCallback((rowKey) => state.selected.has(rowKey), [state.selected]);

  const getSelectedFileIds = useCallback(() => {
    const ids = [];
    state.selected.forEach((key) => {
      (fileIdsRef.current.get(key) || []).forEach((id) => ids.push(id));
    });
    return ids;
  }, [state.selected]);

  return {
    selected: state.selected,
    size: state.selected.size,
    isSelected,
    handleClick,
    clear,
    getSelectedFileIds,
  };
};
